/* ─────────────────────────────────────────────────────────────────────────────
   Secciones del apartado "Configuración" (el negocio, la operación y el equipo).

   Cada sección es autónoma: si necesita datos propios (mesas, métodos de pago)
   los carga y los guarda ella misma contra su servicio. Solo los ajustes que
   viven en la tabla de configuración suben al shell mediante `actualizar`,
   porque esos se guardan todos juntos con el botón "Guardar cambios".
   ───────────────────────────────────────────────────────────────────────── */
import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Pencil, Check, X, RefreshCw } from 'lucide-react';
import api, { metodosPagoService, mesasService, inventarioService } from '../../services/api';
import { espacioLogoPx, espacioLogoArribaPx } from '../../utils/ticket';
import { colorZona } from '../../utils/zonaColor';
import { aplicarTema, coloresDeConfig, contraste, esHex, luminancia, paletaDeFondo } from '../../utils/tema';
import {
  UNIDADES_OBJETIVOS_DEFAULT, UNIDADES_INSUMOS_DEFAULT, UNIDADES_PRODUCTOS_DEFAULT,
  parseCategorias, parseCsv,
} from '../../constants/catalogos';
import {
  CategoriaMovimiento, parseCategoriasMovimiento, claveDesdeNombre,
} from '../../constants/movimientos';
import { normalizarImagenTicket, avisoResolucion } from './imagenes';
import {
  T, Seccion, Tarjeta, Rejilla, Campo, Entrada, EntradaTiempo, Selector, Toggle, FilaToggle, ListaToggles,
  Segmentado, Deslizador, BotonPrimario, BotonSecundario, BotonPeligro, BotonIcono,
  Aviso, ListaChips, Vacio, inputStyle,
} from './ui';

export type PropsConfig = { config: any; actualizar: (k: string, v: string) => void };

/* ═══ NEGOCIO › Identidad ════════════════════════════════════════════════ */
const CAMPOS_NEGOCIO = [
  { key: 'restaurante', label: 'Nombre del restaurante', placeholder: 'Mi Restaurante', ancho: 2 },
  { key: 'slogan',      label: 'Slogan o descripción',   placeholder: 'Cocina de autor',  ancho: 2 },
  { key: 'direccion',   label: 'Dirección',              placeholder: 'Calle, Colonia, Ciudad', ancho: 2 },
  { key: 'telefono',    label: 'Teléfono',               placeholder: '55 1234 5678', ancho: 1 },
  { key: 'rfc',         label: 'RFC',                    placeholder: 'XAXX010101000', ancho: 1 },
];

export function SeccionIdentidad({ config, actualizar }: PropsConfig) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [aviso, setAviso] = useState('');

  const subirLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const { dataUrl, ancho } = await normalizarImagenTicket(file);
    actualizar('logo_base64', dataUrl);
    setAviso(avisoResolucion(ancho));
  };

  return (
    <Seccion>
      <Tarjeta titulo="Datos del negocio" descripcion="Aparecen en el encabezado de los tickets y en el menú lateral del panel.">
        <Rejilla>
          {CAMPOS_NEGOCIO.map(c => (
            <div key={c.key} style={{ gridColumn: c.ancho === 2 ? '1 / -1' : 'auto' }}>
              <Campo label={c.label}>
                <Entrada value={config[c.key] || ''} placeholder={c.placeholder}
                  onChange={e => actualizar(c.key, e.target.value)} />
              </Campo>
            </div>
          ))}
        </Rejilla>
      </Tarjeta>

      <Tarjeta titulo="Logo" descripcion="Se imprime al inicio del ticket y se usa como icono del panel. PNG, JPG o SVG; se convierten a PNG automáticamente.">
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* La caja simula el ancho del ticket para que el % se vea igual que impreso */}
          <div style={{
            width: 200, flexShrink: 0, borderRadius: T.radio, border: `1px solid ${T.borde}`,
            background: 'var(--surface)', padding: 10, minHeight: 90,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {config.logo_base64 ? (
              <img src={config.logo_base64} alt="logo"
                style={{
                  width: `${Math.min(100, Math.max(20, parseInt(String(config.logo_size)) || 70))}%`,
                  height: 'auto', display: 'block',
                  marginTop: espacioLogoArribaPx(config), marginBottom: espacioLogoPx(config),
                }} />
            ) : (
              <span style={{ fontSize: 14, color: T.debil }}>Sin logo</span>
            )}
          </div>

          <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <BotonSecundario onClick={() => fileRef.current?.click()}>Subir imagen</BotonSecundario>
              {config.logo_base64 && (
                <BotonPeligro onClick={() => { actualizar('logo_base64', ''); setAviso(''); }}>Quitar logo</BotonPeligro>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*,.svg" style={{ display: 'none' }} onChange={subirLogo} />
            {aviso && <Aviso tono="alerta">{aviso}</Aviso>}
            {!config.logo_base64 && (
              <p style={{ fontSize: 13, color: T.debil, margin: 0, lineHeight: 1.45 }}>
                Entre más grande el archivo original, más nítido sale impreso. Recomendado: 576 px de ancho o más.
              </p>
            )}
          </div>
        </div>

        {config.logo_base64 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, borderTop: `1px solid ${T.bordeSuave}`, paddingTop: 16 }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: T.texto2, margin: 0 }}>Cómo se imprime en el ticket</p>
            <Deslizador
              label="Tamaño del logo" sufijo="% del ancho del ticket"
              valor={parseInt(String(config.logo_size)) || 70}
              onChange={v => actualizar('logo_size', v)}
              min={20} max={100} step={5} izq="20%" der="100%" />
            <Deslizador
              label="Espacio sobre el logo" sufijo=" px"
              valor={espacioLogoArribaPx(config)}
              onChange={v => actualizar('logo_espacio_arriba', v)}
              min={0} max={40} step={2} izq="Sin margen" der="Muy separado" />
            <Deslizador
              label="Espacio bajo el logo" sufijo=" px"
              valor={espacioLogoPx(config)}
              onChange={v => actualizar('logo_espacio', v)}
              min={0} max={40} step={2} izq="Pegado" der="Muy separado" />
            <Aviso>En la impresora térmica el control solo agrega aire: por debajo de 6 px sale igual que hoy.</Aviso>
          </div>
        )}
      </Tarjeta>

      <Tarjeta titulo="Inicio de la semana" descripcion='En los reportes, "Semana" va de este día a hoy y se compara contra los mismos días de la semana pasada.'>
        <Campo label="La semana empieza el">
          <Selector value={config.inicio_semana ?? '1'} onChange={e => actualizar('inicio_semana', e.target.value)}>
            {['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'].map((d, i) => <option key={i} value={i}>{d}</option>)}
          </Selector>
        </Campo>
      </Tarjeta>
    </Seccion>
  );
}

/* ═══ NEGOCIO › Apariencia ═══════════════════════════════════════════════ */
const PALETA_MARCA = [
  '#DC2626', '#EA580C', '#D97706', '#65A30D',
  '#16A34A', '#0D9488', '#2563EB', '#7C3AED',
  '#C026D3', '#DB2777', '#57534E', '#111827',
];

export function SeccionApariencia({ config, actualizar }: PropsConfig) {
  // Vista previa en vivo: cualquier cambio de tema o color se ve en todo el panel;
  // si se sale sin guardar, Ajustes regresa a lo guardado.
  const { tema, color_primario, color_primario_oscuro, fondo_claro, fondo_oscuro } = config;
  useEffect(() => {
    if (Object.keys(config).length) aplicarTema(tema || 'claro', coloresDeConfig(config));
  }, [tema, color_primario, color_primario_oscuro, fondo_claro, fondo_oscuro]); // eslint-disable-line react-hooks/exhaustive-deps

  const marcaClaro = esHex(color_primario) ? color_primario : FABRICA.marca;
  const marcaOscuro = esHex(color_primario_oscuro) ? color_primario_oscuro : marcaClaro;
  const fClaro = esHex(fondo_claro) ? fondo_claro : FABRICA.fondoClaro;
  const fOscuro = esHex(fondo_oscuro) ? fondo_oscuro : FABRICA.fondoOscuro;

  const avisosClaro = [
    luminancia(fClaro) < 0.45 && 'Este fondo es oscuro para el modo claro: el texto (café oscuro) no se va a leer.',
    contraste(marcaClaro, fClaro) < 3 && 'El color de marca se confunde con el fondo: el menú activo y los enlaces no se van a leer bien.',
  ].filter(Boolean) as string[];
  const avisosOscuro = [
    luminancia(fOscuro) > 0.2 && 'Este fondo es claro para el modo oscuro: el texto (crema) no se va a leer.',
  ].filter(Boolean) as string[];

  return (
    <Seccion>
      <Tarjeta titulo="Tema de la app" descripcion="Oscuro cansa menos la vista en la noche. Los cambios se ven al instante; guarda para que se queden.">
        <Segmentado value={tema === 'oscuro' ? 'oscuro' : 'claro'} onChange={v => actualizar('tema', v)}
          opciones={[{ valor: 'claro', label: 'Claro', hint: 'Fondo claro' }, { valor: 'oscuro', label: 'Oscuro', hint: 'Fondo oscuro' }]} />
      </Tarjeta>

      <Tarjeta titulo="Colores del modo claro" descripcion="La marca se usa en botones, menú activo, enlaces y gráficas, aquí y en la app del mesero.">
        <SelectorColor label="Color de marca" valor={color_primario} porDefecto={FABRICA.marca} paleta={PALETA_MARCA} onCambio={v => actualizar('color_primario', v)} />
        <SelectorColor label="Color de fondo" valor={fondo_claro} porDefecto={FABRICA.fondoClaro} paleta={FONDOS_CLAROS} onCambio={v => actualizar('fondo_claro', v)}
          nota="Tarjetas y líneas se calculan solas a partir del fondo." />
        <MuestraModo fondo={fClaro} marca={marcaClaro} oscuro={false} />
        {avisosClaro.map(a => <Aviso key={a} tono="alerta">{a}</Aviso>)}
      </Tarjeta>

      <Tarjeta titulo="Colores del modo oscuro" descripcion="Si no eliges marca para oscuro, se usa la del modo claro (aclarada para textos y enlaces).">
        <SelectorColor label="Color de marca" valor={color_primario_oscuro} porDefecto={marcaClaro} textoDefecto="Igual que en claro" paleta={PALETA_MARCA_OSCURO} onCambio={v => actualizar('color_primario_oscuro', v)} />
        <SelectorColor label="Color de fondo" valor={fondo_oscuro} porDefecto={FABRICA.fondoOscuro} paleta={FONDOS_OSCUROS} onCambio={v => actualizar('fondo_oscuro', v)}
          nota="Tarjetas y líneas se calculan solas a partir del fondo." />
        <MuestraModo fondo={fOscuro} marca={marcaOscuro} oscuro />
        {avisosOscuro.map(a => <Aviso key={a} tono="alerta">{a}</Aviso>)}
      </Tarjeta>
    </Seccion>
  );
}

// Colores de fábrica (index.css). Vacío en la config = estos.
const FABRICA = { marca: '#B8432F', fondoClaro: '#F5EFE6', fondoOscuro: '#1B1713' };
const FONDOS_CLAROS = ['#F5EFE6', '#FFFFFF', '#F3F4F6', '#EEF2F7', '#F1F5EE', '#FBF1F0'];
const FONDOS_OSCUROS = ['#1B1713', '#111827', '#0F172A', '#18181B', '#14201A', '#1F1428'];
const PALETA_MARCA_OSCURO = ['#E8826E', '#F2B55B', '#7DD3A8', '#7DB6F2', '#C4A5F5', '#F28DB3'];

/** Selector de un color: muestras rápidas + selector nativo + hex, y "usar el de fábrica". */
function SelectorColor({ label, valor, porDefecto, textoDefecto = 'Usar el de fábrica', paleta, nota, onCambio }: {
  label: string; valor?: string; porDefecto: string; textoDefecto?: string; paleta?: string[]; nota?: string; onCambio: (v: string) => void;
}) {
  const actual = esHex(valor) ? valor! : porDefecto;
  const [hex, setHex] = useState(valor || '');
  useEffect(() => { setHex(valor || ''); }, [valor]);
  const escribir = (t: string) => {
    const v = t.trim().startsWith('#') ? t.trim() : `#${t.trim()}`;
    setHex(t);
    if (esHex(v)) onCambio(v.toUpperCase());
  };
  const malo = hex.trim() !== '' && !esHex(hex.trim().startsWith('#') ? hex.trim() : `#${hex.trim()}`);
  return (
    <Campo label={label} hint={nota}>
      {paleta && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          {paleta.map(c => {
            const sel = actual.toLowerCase() === c.toLowerCase();
            return <button key={c} type="button" onClick={() => onCambio(c)} title={c} aria-label={`${label} ${c}`} aria-pressed={sel}
              style={{ width: 40, height: 40, borderRadius: T.radioChico, background: c, cursor: 'pointer',
                border: sel ? '2px solid var(--ink)' : '1px solid var(--line-strong)', boxShadow: sel ? 'inset 0 0 0 2px var(--surface)' : 'none' }} />;
          })}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <input type="color" value={actual} onChange={e => onCambio(e.target.value.toUpperCase())} aria-label={`${label}: selector`}
          style={{ width: 56, height: 48, border: `1px solid ${T.borde}`, borderRadius: T.radioChico, padding: 2, cursor: 'pointer', background: 'var(--surface)', flexShrink: 0 }} />
        <Entrada value={hex} placeholder={porDefecto} onChange={e => escribir(e.target.value)} aria-label={`${label}: hex`} maxLength={7}
          style={{ width: 130, fontFamily: 'monospace', borderColor: malo ? 'var(--danger)' : undefined }} />
        {valor && <BotonSecundario type="button" onClick={() => { setHex(''); onCambio(''); }}>{textoDefecto}</BotonSecundario>}
      </div>
      {malo && <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--danger)' }}>Escribe un color como #1A2B3C.</p>}
    </Campo>
  );
}

/** Muestra de un modo con sus propios colores, sin depender del tema activo. */
function MuestraModo({ fondo, marca, oscuro }: { fondo: string; marca: string; oscuro: boolean }) {
  const p = paletaDeFondo(fondo, oscuro);
  const ink = oscuro ? '#F4EDE3' : '#231F1B', muted = oscuro ? '#B7AB9D' : '#645A50';
  const onBrand = contraste(marca, '#ffffff') >= contraste(marca, '#1B1713') ? '#ffffff' : '#1B1713';
  const texto = oscuro && luminancia(marca) <= 0.3 ? `color-mix(in oklab, ${marca} 40%, white)` : marca;
  return (
    <div aria-label={`Vista previa del modo ${oscuro ? 'oscuro' : 'claro'}`} style={{ background: p['--bg'], borderRadius: 14, padding: 14, border: `1px solid ${p['--line']}` }}>
      <div style={{ background: p['--surface'], border: `1px solid ${p['--line']}`, borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <b style={{ color: ink, fontSize: 16 }}>Ventas por día</b>
          <span style={{ color: texto, fontWeight: 700, fontSize: 14 }}>Ver más →</span>
        </div>
        <span style={{ color: muted, fontSize: 14 }}>Texto secundario sobre la tarjeta</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ background: marca, color: onBrand, padding: '8px 16px', borderRadius: 10, fontWeight: 700, fontSize: 14 }}>Cobrar</span>
          <span style={{ background: p['--well'], color: ink, padding: '8px 16px', borderRadius: 10, fontWeight: 700, fontSize: 14 }}>Secundario</span>
          <span style={{ height: 10, flex: '1 1 80px', borderRadius: 999, background: p['--well'] }}><span style={{ display: 'block', height: '100%', width: '60%', borderRadius: 999, background: texto }} /></span>
        </div>
      </div>
    </div>
  );
}

/* ═══ OPERACIÓN › Mesas ══════════════════════════════════════════════════ */
export function SeccionMesas() {
  const [mesas, setMesas] = useState<any[]>([]);
  const [numero, setNumero] = useState('');
  const [capacidad, setCapacidad] = useState('4');
  const [zona, setZona] = useState('');
  const [error, setError] = useState('');
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [editNumero, setEditNumero] = useState('');
  const [editCapacidad, setEditCapacidad] = useState('');
  const [editZona, setEditZona] = useState('');

  const cargar = async () => setMesas((await mesasService.getAll()).data);
  useEffect(() => { cargar(); }, []);

  // Pedidos colgados (ver mesasController.getFantasmas)
  const [fantasmas, setFantasmas] = useState<any[]>([]);
  const cargarFantasmas = () => api.get('/mesas/fantasmas').then(r => setFantasmas(r.data)).catch(() => {});
  useEffect(() => { cargarFantasmas(); }, []);
  const cerrarFantasmas = async () => {
    if (!window.confirm(`Se cancelarán ${fantasmas.length} pedido(s) sin cobrar.

No cuentan como venta. ¿Continuar?`)) return;
    try { await api.post('/mesas/fantasmas/cerrar'); await cargarFantasmas(); await cargar(); }
    catch (e: any) { setError(e.response?.data?.error || 'No se pudieron cerrar'); }
  };

  /* Las zonas no son un catálogo aparte: son los nombres que ya se escribieron
     en alguna mesa. Se ofrecen en una lista para no escribirlos distinto. */
  const zonas = [...new Set(mesas.map(m => m.zona).filter(Boolean))].sort() as string[];

  const crear = async () => {
    setError('');
    const n = parseInt(numero), c = parseInt(capacidad);
    if (!n || n <= 0) { setError('Número de mesa inválido'); return; }
    if (!c || c <= 0) { setError('Capacidad inválida'); return; }
    try {
      await mesasService.crear(n, c, zona.trim());
      setNumero(''); setCapacidad('4');
      await cargar();
    } catch (e: any) { setError(e.response?.data?.error || 'Error al crear la mesa'); }
  };

  const eliminar = async (m: any) => {
    setError('');
    if (!window.confirm(
      `ELIMINAR LA MESA ${m.numero}\n\n` +
      `Desaparecerá del salón.\n\n` +
      `Sus ventas y tickets anteriores NO se borran: el histórico queda igual.\n\n¿Continuar?`
    )) return;
    try { await mesasService.eliminar(m.id); await cargar(); }
    catch (e: any) { setError(e.response?.data?.error || 'No se pudo eliminar la mesa'); }
  };

  const empezarEditar = (m: any) => {
    setError(''); setEditandoId(m.id);
    setEditNumero(String(m.numero)); setEditCapacidad(String(m.capacidad)); setEditZona(m.zona || '');
  };

  const guardarEditar = async (m: any) => {
    setError('');
    const n = parseInt(editNumero), c = parseInt(editCapacidad);
    if (!n || n <= 0) { setError('Número de mesa inválido'); return; }
    if (!c || c <= 0) { setError('Capacidad inválida'); return; }
    if (n !== m.numero && !window.confirm(
      `La Mesa ${m.numero} pasará a llamarse Mesa ${n}.\n\n` +
      `Los tickets y reportes de antes seguirán diciendo "Mesa ${m.numero}".\n\n¿Continuar?`
    )) return;
    try { await mesasService.actualizar(m.id, { numero: n, capacidad: c, zona: editZona.trim() }); setEditandoId(null); await cargar(); }
    catch (e: any) { setError(e.response?.data?.error || 'No se pudo actualizar la mesa'); }
  };

  return (
    <Seccion>
      <Tarjeta titulo="Agregar mesa" descripcion="Los cambios de esta sección se guardan al instante, no necesitan el botón de arriba.">
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Campo label="Número">
            <Entrada type="number" min={1} value={numero} placeholder="Ej. 12" style={{ width: 110 }}
              onChange={e => setNumero(e.target.value)} onKeyDown={e => e.key === 'Enter' && crear()} />
          </Campo>
          <Campo label="Capacidad">
            <Entrada type="number" min={1} value={capacidad} style={{ width: 110 }}
              onChange={e => setCapacidad(e.target.value)} onKeyDown={e => e.key === 'Enter' && crear()} />
          </Campo>
          <Campo label="Zona (opcional)">
            <Entrada list="zonas-mesas" value={zona} placeholder="Ej. Terraza" style={{ width: 190 }}
              onChange={e => setZona(e.target.value)} onKeyDown={e => e.key === 'Enter' && crear()} />
          </Campo>
          <datalist id="zonas-mesas">{zonas.map(z => <option key={z} value={z} />)}</datalist>
          <BotonPrimario onClick={crear}><Plus size={15} /> Agregar</BotonPrimario>
        </div>
        {error && <Aviso tono="alerta">{error}</Aviso>}
      </Tarjeta>

      <Tarjeta
        titulo={`Mesas del restaurante (${mesas.length})`}
        descripcion="Puedes cambiarle el número, la zona (Terraza, Salón, Barra...) o eliminarla mientras esté libre. Las mesas se agrupan por zona en la Comandera y en el teléfono. Los tickets, cortes y reportes de antes no cambian: siguen diciendo el número que tenían.">
        {mesas.length === 0 && <Vacio>Todavía no hay mesas configuradas</Vacio>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {mesas.map(m => editandoId === m.id ? (
            <div key={m.id} style={{
              display: 'flex', alignItems: 'flex-end', gap: 10, padding: '10px 14px',
              border: `1px solid ${T.marcaTexto}`, borderRadius: T.radio, background: 'var(--well)',
            }}>
              <Campo label="Número">
                <Entrada type="number" min={1} value={editNumero} autoFocus style={{ width: 80 }}
                  onChange={e => setEditNumero(e.target.value)} />
              </Campo>
              <Campo label="Capacidad">
                <Entrada type="number" min={1} value={editCapacidad} style={{ width: 90 }}
                  onChange={e => setEditCapacidad(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && guardarEditar(m)} />
              </Campo>
              <Campo label="Zona">
                <Entrada list="zonas-mesas" value={editZona} placeholder="Sin zona" style={{ width: 170 }}
                  onChange={e => setEditZona(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && guardarEditar(m)} />
              </Campo>
              <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                <BotonPrimario onClick={() => guardarEditar(m)} title="Guardar" style={{ padding: '0 12px' }}><Check size={16} /></BotonPrimario>
                <BotonSecundario onClick={() => { setEditandoId(null); setError(''); }} title="Cancelar" style={{ padding: '0 12px' }}><X size={16} /></BotonSecundario>
              </div>
            </div>
          ) : (
            <div key={m.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              padding: '10px 14px', border: `1px solid ${T.borde}`, borderRadius: T.radio,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: T.texto }}>Mesa {m.numero}</span>
                <span style={{ fontSize: 14, color: T.debil }}>{m.capacidad} personas</span>
                {m.zona && <span style={{
                  fontSize: 13, fontWeight: 700, padding: '2px 9px', borderRadius: 999,
                  background: colorZona(m.zona).claro, color: colorZona(m.zona).fuerte,
                  border: `1px solid ${colorZona(m.zona).fuerte}33`,
                }}>{m.zona}</span>}
                <span style={{
                  fontSize: 13, fontWeight: 700, padding: '2px 8px', borderRadius: 999, textTransform: 'capitalize',
                  background: m.estado === 'libre' ? 'var(--ok-bg)' : 'var(--well)',
                  color: m.estado === 'libre' ? 'var(--ok)' : T.tenue,
                }}>{m.estado}</span>
              </div>
              <div style={{ display: 'flex', gap: 2 }}>
                <BotonIcono onClick={() => empezarEditar(m)} title="Editar"><Pencil size={14} /></BotonIcono>
                {m.estado === 'libre'
                  ? <BotonIcono onClick={() => eliminar(m)} title="Eliminar"><Trash2 size={14} /></BotonIcono>
                  : <span style={{ fontSize: 13, color: 'var(--muted)', alignSelf: 'center', padding: '0 8px' }} title="Solo se puede eliminar una mesa libre">—</span>}
              </div>
            </div>
          ))}
        </div>
      </Tarjeta>
      <Tarjeta
        titulo="Pedidos colgados"
        acciones={<BotonSecundario onClick={cargarFantasmas}><RefreshCw size={14} /> Revisar</BotonSecundario>}
        descripcion="Comandas abiertas y sin pago en una mesa libre o eliminada. Siguen saliendo en cocina/barra como si hubiera pedido, pero no se pueden abrir desde el mapa. Cerrarlas las cancela: no cuentan como venta y el inventario apartado se regresa.">
        {fantasmas.length === 0 ? (
          <Aviso tono="ok">No hay pedidos colgados.</Aviso>
        ) : (
          <>
            {fantasmas.map(f => (
              <div key={f.id} style={{ fontSize: 15, padding: '6px 0', borderBottom: `1px solid ${T.borde}` }}>
                <b>Mesa {f.mesa_numero}</b>{!f.mesa_activa ? ' (eliminada)' : ''} · {f.productos || 'sin productos'}
                <span style={{ color: T.debil }}> · {new Date(f.created_at).toLocaleString('es-MX')}</span>
              </div>
            ))}
            <div style={{ marginTop: 12 }}>
              <BotonPeligro onClick={cerrarFantasmas}>Cerrar {fantasmas.length} pedido(s) colgado(s)</BotonPeligro>
            </div>
          </>
        )}
      </Tarjeta>
    </Seccion>
  );
}

/* ═══ OPERACIÓN › Métodos de pago ════════════════════════════════════════ */
export function SeccionMetodosPago() {
  const [metodos, setMetodos] = useState<any[]>([]);
  const [nombre, setNombre] = useState('');
  const [error, setError] = useState('');

  const cargar = async () => setMetodos((await metodosPagoService.getAll()).data);
  useEffect(() => { cargar(); }, []);

  const crear = async () => {
    setError('');
    if (!nombre.trim()) return;
    const clave = nombre.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
    try { await metodosPagoService.crear({ clave, nombre: nombre.trim() }); setNombre(''); await cargar(); }
    catch (e: any) { setError(e.response?.data?.error || 'Error al crear el método'); }
  };

  const alternar = async (m: any) => { await metodosPagoService.actualizar(m.clave, { activo: !m.activo }); await cargar(); };
  const cambiarClaveSat = async (clave: string, clave_sat_forma_pago: string) => {
    await metodosPagoService.actualizar(clave, { clave_sat_forma_pago });
    await cargar();
  };
  const eliminar = async (clave: string) => {
    setError('');
    try { await metodosPagoService.eliminar(clave); await cargar(); }
    catch (e: any) { setError(e.response?.data?.error || 'No se pudo eliminar'); }
  };

  return (
    <Seccion>
      <Tarjeta titulo="Agregar método" descripcion="Los cambios de esta sección se guardan al instante, no necesitan el botón de arriba.">
        <div style={{ display: 'flex', gap: 8 }}>
          <Entrada value={nombre} placeholder="Ej: Mercado Pago QR"
            onChange={e => setNombre(e.target.value)} onKeyDown={e => e.key === 'Enter' && crear()} />
          <BotonPrimario onClick={crear} disabled={!nombre.trim()}><Plus size={15} /> Agregar</BotonPrimario>
        </div>
        {error && <Aviso tono="alerta">{error}</Aviso>}
      </Tarjeta>

      <Tarjeta
        titulo="Métodos disponibles al cobrar"
        descripcion='Aparecen en Caja y en el corte de turno. "Efectivo" es el fondo físico de la caja: no se puede desactivar ni eliminar.'>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {metodos.map(m => (
            <div key={m.clave} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              padding: '10px 14px', border: `1px solid ${T.borde}`, borderRadius: T.radio, flexWrap: 'wrap',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: m.color, flexShrink: 0 }} />
                <span style={{ fontSize: 16, fontWeight: 500, color: m.activo ? T.texto : T.debil }}>{m.nombre}</span>
                {m.sistema && (
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.debil, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                    del sistema
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label title="Clave SAT c_FormaPago para facturación electrónica"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: T.debil }}>
                  CFDI
                  <input value={m.clave_sat_forma_pago || '01'} onChange={e => cambiarClaveSat(m.clave, e.target.value)}
                    style={{ ...inputStyle, width: 64, height: 40, padding: '0 6px', textAlign: 'center' }} />
                </label>
                <Toggle value={m.activo ? 'true' : 'false'} onChange={() => alternar(m)} />
                {!m.sistema
                  ? <BotonIcono onClick={() => eliminar(m.clave)} title="Eliminar"><Trash2 size={14} /></BotonIcono>
                  : <span style={{ width: 30 }} />}
              </div>
            </div>
          ))}
        </div>
        <Aviso>
          La clave CFDI es la forma de pago del SAT que se usa al facturar: 01 efectivo, 03 transferencia,
          04 tarjeta de crédito, 28 tarjeta de débito.
        </Aviso>
      </Tarjeta>
    </Seccion>
  );
}

/* Las listas de unidades funcionan igual: CSV en una sola clave. */
const listaCsv = (config: any, actualizar: PropsConfig['actualizar'], clave: string, porDefecto: string[]) => ({
  items: parseCsv(config[clave], porDefecto),
  onAdd: (v: string) => actualizar(clave, [...parseCsv(config[clave], porDefecto), v].join(',')),
  onRemove: (v: string) => actualizar(clave, parseCsv(config[clave], porDefecto).filter(x => x !== v).join(',')),
});

/* ═══ SALÓN › Reservaciones ═══════════════════════════════════════════════ */
export function SeccionReservaciones({ config, actualizar }: PropsConfig) {
  return (
    <Seccion>
      <Tarjeta titulo="Reservaciones" descripcion="Dos reservaciones de la misma mesa más cerca que esto se consideran encimadas y no se permiten.">
        <Campo label="Margen entre reservaciones">
          <EntradaTiempo guardadoEn="min" min={1} valor={config.reservas_margen_min ?? '90'}
              onChange={v => actualizar('reservas_margen_min', v)} />
        </Campo>
      </Tarjeta>
    </Seccion>
  );
}

/* ═══ SALÓN › App del mesero ══════════════════════════════════════════════
   bloqueo_gracia_seg → AuthContext del teléfono; notas_max_* → mesas.tsx y backend. */
export function SeccionAppMesero({ config, actualizar }: PropsConfig) {
  return (
    <Seccion>
      <Tarjeta titulo="Bloqueo de la app del mesero" descripcion="Si el teléfono sale de la app más de este tiempo, al volver pide huella o PIN.">
        <Campo label="Tiempo fuera de la app" hint="0 = siempre pide. Máximo 10 min.">
          <EntradaTiempo guardadoEn="seg" min={0} valor={config.bloqueo_gracia_seg ?? '60'}
              onChange={v => actualizar('bloqueo_gracia_seg', v)} />
        </Campo>
      </Tarjeta>

      <Tarjeta titulo="Notas del mesero" descripcion="Máximo de caracteres que puede escribir el mesero en una nota. Tope 2000.">
        <Rejilla>
          <Campo label="Nota por producto">
            <Entrada type="number" min={1} max={2000} value={config.notas_max_producto ?? '300'}
              onChange={e => actualizar('notas_max_producto', e.target.value)} />
          </Campo>
          <Campo label="Nota del pedido completo">
            <Entrada type="number" min={1} max={2000} value={config.notas_max_pedido ?? '500'}
              onChange={e => actualizar('notas_max_pedido', e.target.value)} />
          </Campo>
        </Rejilla>
      </Tarjeta>
    </Seccion>
  );
}

/* ═══ CAJA › Botones rápidos (caja_propinas_pct / caja_descuentos_pct) ════ */
export function SeccionBotonesCaja({ config, actualizar }: PropsConfig) {
  return (
    <Seccion>
      <Tarjeta titulo="Redondeo" descripcion="Para no manejar centavos en efectivo.">
        <ListaToggles>
          <FilaToggle label="Redondear totales" desc="Redondea el total de cada cuenta al peso entero más cercano ($125.40 → $125, $125.60 → $126). El ticket muestra la diferencia como «Redondeo»."
            cfgKey="caja_redondeo" config={config} actualizar={actualizar} porDefecto="false" />
        </ListaToggles>
      </Tarjeta>
      <Tarjeta titulo="Botones rápidos en Caja" descripcion="Porcentajes separados por coma. Aparecen como botones al cobrar.">
        <Rejilla>
          <Campo label="Propina (%)" hint="Ej. 10,15,20">
            <Entrada value={config.caja_propinas_pct ?? '10,15,20'} placeholder="10,15,20"
              onChange={e => actualizar('caja_propinas_pct', e.target.value)} />
          </Campo>
          <Campo label="Descuento por producto (%)" hint="Ej. 10,25,50">
            <Entrada value={config.caja_descuentos_pct ?? '10,25,50'} placeholder="10,25,50"
              onChange={e => actualizar('caja_descuentos_pct', e.target.value)} />
          </Campo>
        </Rejilla>
      </Tarjeta>
    </Seccion>
  );
}

/* ═══ CAJA › Movimientos de caja ══════════════════════════════════════════ */
export function SeccionMovimientosCaja({ config, actualizar }: PropsConfig) {
  /* Categorías de movimientos de caja (Caja › Registrar movimiento). Mismo
     mecanismo que las de objetivos: un JSON en configuracion_ticket. */
  const catsMov = parseCategoriasMovimiento(config.movimientos_categorias);
  const guardarCatsMov = (nuevas: CategoriaMovimiento[]) =>
    actualizar('movimientos_categorias', JSON.stringify(nuevas));
  const editarCatMov = (value: string, campo: 'label' | 'emoji' | 'aplica', val: string) =>
    guardarCatsMov(catsMov.map(c => c.value === value ? { ...c, [campo]: val } : c));
  const eliminarCatMov = (value: string) => guardarCatsMov(catsMov.filter(c => c.value !== value));

  const [nuevoMov, setNuevoMov] = useState('');
  const [nuevoMovAplica, setNuevoMovAplica] = useState('retiro');
  const agregarCatMov = () => {
    const label = nuevoMov.trim();
    // La clave es la que se guarda en la base y no cambia aunque después se
    // renombre la categoría: así los movimientos viejos no se quedan huérfanos.
    const value = claveDesdeNombre(label);
    if (!value || catsMov.some(c => c.value === value)) return;
    guardarCatsMov([...catsMov, {
      value, label, emoji: '📌', aplica: nuevoMovAplica as any, sistema: false,
    }]);
    setNuevoMov('');
  };

  return (
    <Seccion>
      <Tarjeta
        titulo="Categorías de movimientos de caja"
        descripcion='Los conceptos que se eligen al registrar un retiro o un depósito en Caja. Con ellos se agrupa el reporte de ventas y el corte impreso. "Propina a mesero" y "Otro" no se pueden eliminar: la primera es la que clasifica sola lo que se le paga a los meseros y la segunda es donde cae todo lo que no se clasificó. Si eliminas una categoría, los movimientos ya registrados con ella pasan a verse como "Otro".'>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {catsMov.map(c => (
            <div key={c.value} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
              border: `1px solid ${T.borde}`, borderRadius: T.radio,
            }}>
              <input value={c.emoji} maxLength={2} title="Icono"
                onChange={e => editarCatMov(c.value, 'emoji', e.target.value)}
                style={{ width: 34, textAlign: 'center', border: 'none', outline: 'none', fontSize: 16, background: 'transparent', flexShrink: 0 }} />
              <input value={c.label} onChange={e => editarCatMov(c.value, 'label', e.target.value)}
                style={{ flex: 1, border: 'none', outline: 'none', fontSize: 15, fontFamily: 'inherit', color: T.texto2, minWidth: 0, background: 'transparent' }} />
              <Selector value={c.aplica} onChange={e => editarCatMov(c.value, 'aplica', e.target.value)}
                style={{ width: 150, flexShrink: 0 }}>
                <option value="retiro">Retiros</option>
                <option value="deposito">Depósitos</option>
                <option value="ambos">Los dos</option>
              </Selector>
              {c.sistema
                ? <span style={{ fontSize: 12, fontWeight: 700, color: T.debil, textTransform: 'uppercase', letterSpacing: '.04em', flexShrink: 0 }}>del sistema</span>
                : <BotonIcono onClick={() => eliminarCatMov(c.value)} title="Eliminar"><Trash2 size={13} /></BotonIcono>}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Entrada value={nuevoMov} placeholder="Ej: Renta del local" style={{ flex: '1 1 180px' }}
            onChange={e => setNuevoMov(e.target.value)} onKeyDown={e => e.key === 'Enter' && agregarCatMov()} />
          <Selector value={nuevoMovAplica} onChange={e => setNuevoMovAplica(e.target.value)} style={{ width: 132 }}>
            <option value="retiro">Retiros</option>
            <option value="deposito">Depósitos</option>
            <option value="ambos">Los dos</option>
          </Selector>
          <BotonPrimario onClick={agregarCatMov} disabled={!nuevoMov.trim()}><Plus size={15} /> Agregar</BotonPrimario>
        </div>
      </Tarjeta>
    </Seccion>
  );
}

/* ═══ PRODUCTOS › Productos (iva_default, productos_unidades) ═════════════ */
export function SeccionProductos({ config, actualizar }: PropsConfig) {
  return (
    <Seccion>
      <Tarjeta titulo="IVA de productos nuevos" descripcion="Con qué tasa se llena el campo IVA al dar de alta un producto. Cada producto puede cambiarla después.">
        <Campo label="Tasa (%)" hint="16 general · 8 zona fronteriza · 0 exentos">
          <Entrada type="number" min={0} max={16} step="0.01" value={config.iva_default ?? '16'}
            onChange={e => actualizar('iva_default', e.target.value)} />
        </Campo>
      </Tarjeta>

      <Tarjeta titulo="Unidades de productos" descripcion="Se usan al dar de alta un producto del menú.">
        <ListaChips {...listaCsv(config, actualizar, 'productos_unidades', UNIDADES_PRODUCTOS_DEFAULT)} />
      </Tarjeta>
    </Seccion>
  );
}

/* ═══ PRODUCTOS › Comida de empleados (comida_limite_dia → routes/comidaEmpleados.js) ═══ */
export function SeccionComidaEmpleados({ config, actualizar }: PropsConfig) {
  return (
    <Seccion>
      <Tarjeta titulo="Límite por día" descripcion="Cuántas comidas aprobadas puede tener cada empleado en un día. Si se pasa, quien autoriza puede aprobar una extra y queda registrado en la auditoría.">
        <Campo label="Comidas por empleado al día" hint="0 = sin límite.">
          <Entrada type="number" min={0} value={config.comida_limite_dia ?? '1'}
            onChange={e => actualizar('comida_limite_dia', e.target.value)} />
        </Campo>
      </Tarjeta>
      <Aviso>Quién puede autorizar se elige en Usuarios, con el permiso «Autorizar comida de empleados».</Aviso>
    </Seccion>
  );
}

/* ═══ OBJETIVOS SMART ═════════════════════════════════════════════════════ */
export function SeccionObjetivos({ config, actualizar }: PropsConfig) {
  const categorias = parseCategorias(config.objetivos_categorias);
  const guardarCategorias = (nuevas: typeof categorias) => actualizar('objetivos_categorias', JSON.stringify(nuevas));
  const editarCategoria = (value: string, campo: 'label' | 'color', val: string) =>
    guardarCategorias(categorias.map(c => c.value === value ? { ...c, [campo]: val } : c));
  const eliminarCategoria = (value: string) => guardarCategorias(categorias.filter(c => c.value !== value));

  const [nuevaCategoria, setNuevaCategoria] = useState('');
  const agregarCategoria = () => {
    const label = nuevaCategoria.trim();
    if (!label) return;
    const value = label.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    if (!value || categorias.some(c => c.value === value)) return;
    guardarCategorias([...categorias, { value, label, color: 'var(--muted)', sistema: false }]);
    setNuevaCategoria('');
  };

  return (
    <Seccion>
      <Tarjeta
        titulo="Categorías de Objetivos SMART"
        descripcion='"Ventas", "Comandas" y "Clientes" se calculan automáticamente y no se pueden eliminar; solo cambiar su nombre y color.'>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {categorias.map(c => (
            <div key={c.value} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px',
              border: `1px solid ${T.borde}`, borderRadius: T.radio,
            }}>
              <input type="color" value={c.color} onChange={e => editarCategoria(c.value, 'color', e.target.value)}
                style={{ width: 40, height: 40, border: 'none', borderRadius: 8, padding: 0, cursor: 'pointer', flexShrink: 0, background: 'transparent' }} />
              <input value={c.label} onChange={e => editarCategoria(c.value, 'label', e.target.value)}
                style={{ flex: 1, border: 'none', outline: 'none', fontSize: 15, fontFamily: 'inherit', color: T.texto2, minWidth: 0, background: 'transparent' }} />
              {c.sistema
                ? <span style={{ fontSize: 12, fontWeight: 700, color: T.debil, textTransform: 'uppercase', letterSpacing: '.04em', flexShrink: 0 }}>del sistema</span>
                : <BotonIcono onClick={() => eliminarCategoria(c.value)} title="Eliminar"><Trash2 size={13} /></BotonIcono>}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Entrada value={nuevaCategoria} placeholder="Ej: 🚚 Delivery"
            onChange={e => setNuevaCategoria(e.target.value)} onKeyDown={e => e.key === 'Enter' && agregarCategoria()} />
          <BotonPrimario onClick={agregarCategoria} disabled={!nuevaCategoria.trim()}><Plus size={15} /> Agregar</BotonPrimario>
        </div>
      </Tarjeta>


      <Tarjeta titulo="Unidades de Objetivos SMART" descripcion="Opciones del selector de unidad al crear un objetivo.">
        <ListaChips {...listaCsv(config, actualizar, 'objetivos_unidades', UNIDADES_OBJETIVOS_DEFAULT)} />
      </Tarjeta>

      <Tarjeta titulo="Actualización automática" descripcion="Cada cuánto se actualiza sola la pantalla de Objetivos.">
        <Campo label="Actualizar cada" hint="Mínimo 10 seg.">
          <EntradaTiempo guardadoEn="seg" min={10} valor={config.objetivos_refresco_seg ?? '60'}
              onChange={v => actualizar('objetivos_refresco_seg', v)} />
        </Campo>
      </Tarjeta>
    </Seccion>
  );
}

/* ═══ OPERACIÓN › Inventario ═════════════════════════════════════════════
   "Apartado" = lo que las cuentas abiertas ya tienen reservado del inventario
   (pre-descuento, migración 021). Si ese número se desfasa —se reinició el
   servidor a media cuenta, se editó algo a mano— el disponible deja de cuadrar.
   Reiniciarlo lo vuelve a contar desde cero a partir de las cuentas abiertas.  */
export function SeccionInventario({ config, actualizar }: PropsConfig) {
  const [reservas, setReservas] = useState<any[]>([]);
  const [trabajando, setTrabajando] = useState(false);
  const [aviso, setAviso] = useState<{ tono: 'ok' | 'alerta'; texto: string } | null>(null);

  const cargar = async () => {
    try { setReservas((await inventarioService.getReservas()).data); } catch { /* sin permiso: el botón sigue sirviendo */ }
  };
  useEffect(() => { cargar(); }, []);

  const reiniciar = async () => {
    if (!window.confirm(
      'REINICIAR APARTADOS\n\n' +
      'Vuelve a contar lo que las cuentas abiertas tienen apartado del inventario.\n\n' +
      'NO cancela ninguna cuenta.\nNO cambia el stock físico.\n\n¿Continuar?'
    )) return;
    setTrabajando(true); setAviso(null);
    try {
      const resultado = (await inventarioService.recalcularReservas()).data ?? {};
      const n = resultado.insumos_ajustados ?? 0;
      const liberadas = resultado.reservas_obsoletas_liberadas ?? 0;
      await cargar();
      const partes = [
        n > 0 ? `Se corrigieron ${n} insumo${n > 1 ? 's' : ''}.` : '',
        liberadas > 0 ? `Se liberaron ${liberadas} apartado${liberadas > 1 ? 's' : ''} de cuentas ya cerradas.` : '',
      ].filter(Boolean);
      setAviso({ tono: 'ok', texto: partes.join(' ') || 'Todo estaba cuadrado. No hubo nada que corregir.' });
    } catch (e: any) {
      setAviso({ tono: 'alerta', texto: e.response?.data?.error || 'No se pudo reiniciar los apartados' });
    } finally { setTrabajando(false); }
  };

  return (
    <Seccion>
      <Tarjeta titulo="Unidades de insumos" descripcion="Se usan en Inventario, Mermas y Requisiciones.">
        <ListaChips {...listaCsv(config, actualizar, 'inventario_unidades', UNIDADES_INSUMOS_DEFAULT)} />
      </Tarjeta>

      <Tarjeta
        titulo="Reiniciar apartados"
        descripcion="Úsalo cuando el disponible del inventario no cuadre con lo que hay de verdad. Vuelve a contar lo apartado por las cuentas abiertas. No cancela cuentas ni cambia el stock físico.">
        <BotonPrimario onClick={reiniciar} disabled={trabajando} style={{ fontSize: 15, height: 46, padding: '0 22px' }}>
          <RefreshCw size={18} /> {trabajando ? 'Reiniciando…' : 'Reiniciar apartados'}
        </BotonPrimario>
        {aviso && <Aviso tono={aviso.tono}>{aviso.texto}</Aviso>}
      </Tarjeta>

      <Tarjeta
        titulo={`Apartado ahora mismo (${reservas.length})`}
        descripcion="Lo que las cuentas abiertas tienen reservado. Se libera solo al cobrar o al cancelar la cuenta.">
        {reservas.length === 0 && <Vacio>No hay nada apartado: todas las cuentas están cerradas</Vacio>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {reservas.map(r => (
            <div key={r.insumo_id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              padding: '10px 14px', border: `1px solid ${T.borde}`, borderRadius: T.radio,
            }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: T.texto }}>{r.insumo}</span>
              <span style={{ fontSize: 15, color: T.debil }}>
                {parseFloat(r.cantidad_apartada).toFixed(2)} {r.unidad}
                {r.mesas?.length ? ` · Mesa${r.mesas.length > 1 ? 's' : ''} ${r.mesas.join(', ')}` : ''}
              </span>
            </div>
          ))}
        </div>
      </Tarjeta>
    </Seccion>
  );
}

/* ═══ EQUIPO › Impresión y cajón ═════════════════════════════════════════ */
const IMPRESORAS_RED = [
  { key: 'impresora_cocina_ip', label: 'Cocina', placeholder: '192.168.1.100' },
  { key: 'impresora_barra_ip',  label: 'Barra',  placeholder: '192.168.1.101' },
  { key: 'impresora_caja_ip',   label: 'Caja',   placeholder: '192.168.1.102' },
];

export function SeccionImpresion({ config, actualizar }: PropsConfig) {
  const [modal, setModal] = useState(false);
  const [pass, setPass] = useState('');
  const [msg, setMsg] = useState('');
  const [abriendo, setAbriendo] = useState(false);

  const probarCajon = async () => {
    if (!pass.trim()) { setMsg('Ingresa tu contraseña'); return; }
    setAbriendo(true); setMsg('');
    try {
      const res = await api.post('/configuracion/abrir-cajon-manual', { password: pass });
      if (res.data?.ok) {
        setMsg('✓ Cajón abierto');
        setTimeout(() => { setModal(false); setPass(''); setMsg(''); }, 1500);
      } else { setMsg(res.data?.msg || 'No se pudo abrir'); }
    } catch (e: any) {
      setMsg(e.response?.data?.error || 'Error de conexión');
    } finally { setAbriendo(false); }
  };

  return (
    <Seccion>
      <Tarjeta titulo="Ancho de papel" descripcion="Aplica al ticket de venta, la pre-cuenta y las reimpresiones.">
        <Segmentado
          value={config.ticket_ancho || '80mm'}
          onChange={v => actualizar('ticket_ancho', v)}
          opciones={[
            { valor: '80mm', label: '80 mm', hint: 'Rollo estándar' },
            { valor: '60mm', label: '60 mm', hint: 'Rollo intermedio' },
            { valor: '58mm', label: '58 mm', hint: 'Rollo angosto' },
          ]} />
      </Tarjeta>

      <Tarjeta titulo="Impresoras de red (TCP/IP)" descripcion="Dirección IP de cada estación. Puerto por defecto: 9100.">
        {IMPRESORAS_RED.map(f => (
          <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ fontSize: 14, color: T.texto2, width: 60, flexShrink: 0 }}>{f.label}</label>
            <Entrada value={config[f.key] || ''} placeholder={f.placeholder}
              onChange={e => actualizar(f.key, e.target.value)} />
            {f.key === 'impresora_caja_ip' && (
              <Entrada value={config.impresora_caja_puerto || ''} placeholder="9100" title="Puerto TCP"
                style={{ width: 90, flexShrink: 0 }}
                onChange={e => actualizar('impresora_caja_puerto', e.target.value)} />
            )}
          </div>
        ))}
      </Tarjeta>

      <Tarjeta titulo="Impresora de caja por USB" descripcion="Solo si la impresora de caja está conectada por cable en lugar de red.">
        <Campo label="Nombre de la impresora" hint="Debe coincidir exactamente con el de Panel de control → Dispositivos e impresoras.">
          <Entrada value={config.impresora_caja_nombre || ''} placeholder="Ej: EPSON TM-T20III"
            onChange={e => actualizar('impresora_caja_nombre', e.target.value)} />
        </Campo>
      </Tarjeta>

      <Tarjeta titulo="Cajón de dinero"
        acciones={<Toggle value={config.abrir_cajon ?? 'false'} onChange={v => actualizar('abrir_cajon', v)} />}
        descripcion="Envía el pulso ESC/POS a la impresora de caja al imprimir el ticket de venta.">
        {config.abrir_cajon === 'true' && (
          <>
            {/* Con efectivo el cajón se abre siempre (hay que dar cambio); lo
                que se elige aquí es qué pasa cuando nadie toca dinero. */}
            <ListaToggles>
              <FilaToggle
                label="Abrir también con tarjeta o transferencia"
                desc="En efectivo el cajón se abre siempre. Actívalo solo si además quieres que se abra cuando el cobro es únicamente con tarjeta, transferencia o QR."
                cfgKey="abrir_cajon_sin_efectivo" config={config} actualizar={actualizar} porDefecto="false" />
            </ListaToggles>
            <BotonSecundario onClick={() => { setModal(true); setPass(''); setMsg(''); }} style={{ alignSelf: 'flex-start' }}>
              Probar apertura de cajón
            </BotonSecundario>
          </>
        )}
      </Tarjeta>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
          <div style={{ background: 'var(--surface)', borderRadius: 16, padding: 24, width: '100%', maxWidth: 384, boxShadow: '0 20px 25px -5px rgba(0,0,0,.3)' }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: T.texto, margin: '0 0 4px' }}>Abrir cajón</h3>
            <p style={{ fontSize: 14, color: T.debil, margin: '0 0 16px' }}>Ingresa tu contraseña para confirmar</p>
            <Entrada type="password" autoFocus placeholder="Contraseña" value={pass}
              onChange={e => setPass(e.target.value)} onKeyDown={e => e.key === 'Enter' && probarCajon()} />
            {msg && (
              <p style={{ fontSize: 15, margin: '12px 0 0', color: msg.startsWith('✓') ? 'var(--ok)' : 'var(--danger)' }}>{msg}</p>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <BotonSecundario onClick={() => setModal(false)} style={{ flex: 1 }}>Cancelar</BotonSecundario>
              <BotonPrimario onClick={probarCajon} disabled={abriendo} style={{ flex: 1, background: abriendo ? 'var(--line)' : 'var(--ok)' }}>
                {abriendo ? 'Abriendo…' : 'Abrir'}
              </BotonPrimario>
            </div>
          </div>
        </div>
      )}
    </Seccion>
  );
}

/* ═══ OPERACIÓN › Comisiones de terminal ═════════════════════════════════ */
/* Tasas publicadas por cada proveedor, consultadas en agosto de 2026. Son solo
   el punto de partida del formulario: el porcentaje que se guarda es el que el
   usuario deje en el campo, porque cada contrato termina siendo distinto. */
const TERMINALES = [
  { id: 'kiwi',        nombre: 'KiWi',               pct: '3.2',  fijo: '0', iva: true,  detalle: '3.2 % + IVA' },
  { id: 'mercadopago', nombre: 'Mercado Pago Point', pct: '3.5',  fijo: '0', iva: true,  detalle: '3.5 % + IVA' },
  { id: 'clip',        nombre: 'Clip',               pct: '3.6',  fijo: '0', iva: true,  detalle: '3.6 % + IVA' },
  { id: 'clip_promo',  nombre: 'Clip tasa promo',    pct: '2.99', fijo: '1', iva: true,  detalle: '2.99 % + $1 + IVA' },
  { id: 'bbva',        nombre: 'BBVA (bancaria)',    pct: '2.15', fijo: '0', iva: true,  detalle: 'desde 2.15 % + IVA' },
  { id: 'personalizada', nombre: 'Otra terminal',    pct: '',     fijo: '',  iva: true,  detalle: 'pon tu tasa a mano' },
];

const IVA_COMISION = 0.16;

/* Recordatorio del KDS: la app de cocina/barra lee estas dos claves y repite
   el beep de comanda nueva mientras haya alguna sin "Iniciar". */
export function SeccionKds({ config, actualizar }: PropsConfig) {
  const activo = config.kds_recordatorio === 'true';
  return (
    <Seccion>
      <Tarjeta
        titulo="Repetir la alarma si no le dan Iniciar"
        acciones={<Toggle value={config.kds_recordatorio ?? 'false'} onChange={v => actualizar('kds_recordatorio', v)} />}
        descripcion="Mientras una comanda siga pendiente en cocina o barra, la pantalla vuelve a sonar la misma alarma de comanda nueva hasta que alguien le dé Iniciar.">
        {activo && (
          <Campo label="Sonar cada" hint="Aplica en cocina y barra. La tablet toma el cambio en menos de un minuto.">
            <EntradaTiempo guardadoEn="min" min={0.25} valor={config.kds_recordatorio_min ?? '1'}
              onChange={v => actualizar('kds_recordatorio_min', v)} />
          </Campo>
        )}
      </Tarjeta>

      <Tarjeta
        titulo="Colores por tiempo de espera"
        descripcion="Cada tarjeta del KDS (tablet y PC) cambia de color según los minutos que lleva desde que llegó la comanda: verde, luego ámbar y al final rojo.">
        <Rejilla>
          <Campo label="🟢 → 🟡 Ámbar a partir de">
            <EntradaTiempo guardadoEn="min" min={0.5} valor={config.kds_ambar_min ?? '10'}
              onChange={v => actualizar('kds_ambar_min', v)} />
          </Campo>
          <Campo label="🟡 → 🔴 Rojo a partir de" hint="Debe ser mayor que el de ámbar.">
            <EntradaTiempo guardadoEn="min" min={1} valor={config.kds_rojo_min ?? '20'}
              onChange={v => actualizar('kds_rojo_min', v)} />
          </Campo>
        </Rejilla>
      </Tarjeta>

      <Tarjeta
        titulo="Sonido"
        acciones={<Toggle value={config.kds_sonido ?? 'true'} onChange={v => actualizar('kds_sonido', v)} />}
        descripcion="Alarmas de comanda nueva, ronda extra y cancelación, en tablet y PC.">
        {config.kds_sonido !== 'false' && (
          <Campo label={`Volumen: ${config.kds_volumen ?? '100'} %`}>
            <input type="range" min={10} max={100} step={10} value={config.kds_volumen ?? '100'}
              onChange={e => actualizar('kds_volumen', e.target.value)} style={{ width: '100%' }} />
          </Campo>
        )}
      </Tarjeta>

      <Tarjeta titulo="Tiempos de pantalla">
        <Rejilla>
          <Campo label="Comanda terminada visible" hint="Después pasa a «Ver terminadas». Solo en la PC; la tablet la quita al instante.">
            <EntradaTiempo guardadoEn="seg" min={0} valor={config.kds_terminadas_seg ?? '60'}
              onChange={v => actualizar('kds_terminadas_seg', v)} />
          </Campo>
          <Campo label="Revisar comandas cada" hint="Respaldo por si se pierde la conexión en vivo. Mínimo 5 seg.">
            <EntradaTiempo guardadoEn="seg" min={5} valor={config.kds_refresco_seg ?? '10'}
              onChange={v => actualizar('kds_refresco_seg', v)} />
          </Campo>
        </Rejilla>
      </Tarjeta>
    </Seccion>
  );
}

export function SeccionComisiones({ config, actualizar }: PropsConfig) {
  const [metodos, setMetodos] = useState<any[]>([]);
  useEffect(() => { metodosPagoService.getAll().then(r => setMetodos(r.data)); }, []);

  const activas = config.comisiones_activas === 'true';
  const conIva = config.comisiones_iva !== 'false';
  const pct = parseFloat(config.comisiones_pct) || 0;
  const fijo = parseFloat(config.comisiones_fijo) || 0;
  const seleccionados = (config.comisiones_metodos || 'tarjeta').split(',').map((m: string) => m.trim()).filter(Boolean);
  const absorbe = config.comisiones_absorbe == null ? 100 : (parseFloat(config.comisiones_absorbe) || 0);

  const elegirTerminal = (t: typeof TERMINALES[number]) => {
    actualizar('comisiones_terminal', t.id);
    if (t.id === 'personalizada') return;   // conserva lo que el usuario ya tenía escrito
    actualizar('comisiones_pct', t.pct);
    actualizar('comisiones_fijo', t.fijo);
    actualizar('comisiones_iva', t.iva ? 'true' : 'false');
  };

  const alternarMetodo = (clave: string) => {
    const nuevos = seleccionados.includes(clave)
      ? seleccionados.filter((m: string) => m !== clave)
      : [...seleccionados, clave];
    actualizar('comisiones_metodos', nuevos.join(','));
  };

  /* Ejemplo con $1,000 en un solo cobro: es la forma más rápida de ver si la
     tasa que quedó capturada es la que cobra de verdad la terminal. */
  const comisionEjemplo = (1000 * (pct / 100) + fijo) * (conIva ? 1 + IVA_COMISION : 1);
  const pctEfectivo = conIva ? pct * (1 + IVA_COMISION) : pct;
  const moneda = (n: number) => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Seccion>
      <Tarjeta
        titulo="Descontar comisiones en el corte de turno"
        acciones={<Toggle value={config.comisiones_activas ?? 'false'} onChange={v => actualizar('comisiones_activas', v)} />}
        descripcion="El corte muestra cuánto se queda la terminal de lo cobrado con tarjeta y cuánto va a caer realmente a tu cuenta. La venta registrada no cambia: la comisión es un gasto, no un descuento sobre lo vendido.">
        {!activas && <Aviso>Mientras esté apagado, el corte se imprime igual que hoy.</Aviso>}
      </Tarjeta>

      {activas && (
        <>
          <Tarjeta titulo="Tu terminal" descripcion="Elige la que usas y se llenan las tasas. Puedes ajustarlas después: lo que manda es lo que quede en los campos de abajo.">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))', gap: 8 }}>
              {TERMINALES.map(t => {
                const sel = (config.comisiones_terminal || 'personalizada') === t.id;
                return (
                  <button key={t.id} type="button" onClick={() => elegirTerminal(t)}
                    style={{
                      textAlign: 'left', padding: '11px 14px', borderRadius: T.radio, cursor: 'pointer',
                      fontFamily: 'inherit', transition: 'all .12s',
                      border: `1px solid ${sel ? T.marcaTexto : T.borde}`,
                      background: sel ? 'var(--well)' : 'var(--surface)',
                      color: sel ? 'var(--ink)' : T.tenue,
                    }}>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>{t.nombre}</div>
                    <div style={{ fontSize: 13, marginTop: 2, opacity: .85 }}>{t.detalle}</div>
                  </button>
                );
              })}
            </div>
            <Aviso>
              Tasas publicadas por cada proveedor y consultadas en agosto de 2026. Cambian seguido y muchos
              contratos traen tasa negociada, así que confírmalas en tu estado de cuenta.
            </Aviso>
          </Tarjeta>

          <Tarjeta titulo="Tasa que se aplica">
            <Rejilla>
              <Campo label="Porcentaje por cobro" hint="Tal como lo publica la terminal, sin sumarle el IVA.">
                <Entrada type="number" step="0.01" min={0} value={config.comisiones_pct ?? ''} placeholder="3.6"
                  onChange={e => { actualizar('comisiones_pct', e.target.value); actualizar('comisiones_terminal', 'personalizada'); }} />
              </Campo>
              <Campo label="Cargo fijo por cobro" hint="En pesos. Déjalo en 0 si tu terminal no cobra fijo.">
                <Entrada type="number" step="0.01" min={0} value={config.comisiones_fijo ?? ''} placeholder="0"
                  onChange={e => { actualizar('comisiones_fijo', e.target.value); actualizar('comisiones_terminal', 'personalizada'); }} />
              </Campo>
            </Rejilla>
            <ListaToggles>
              <FilaToggle
                label="Sumar IVA a la comisión"
                desc="Casi todas publican su tasa como “+ IVA”. Apágalo solo si el porcentaje que capturaste ya lo trae incluido."
                cfgKey="comisiones_iva" config={config} actualizar={actualizar} />
            </ListaToggles>
          </Tarjeta>

          <Tarjeta titulo="A qué cobros aplica" descripcion="Normalmente solo la tarjeta. El efectivo no paga comisión y la transferencia depende de tu banco.">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {metodos.filter(m => m.activo).map(m => {
                const sel = seleccionados.includes(m.clave);
                return (
                  <button key={m.clave} type="button" onClick={() => alternarMetodo(m.clave)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 7, padding: '7px 13px', borderRadius: 999,
                      cursor: 'pointer', fontFamily: 'inherit', fontSize: 15, fontWeight: sel ? 600 : 400,
                      border: `1px solid ${sel ? T.marcaTexto : T.borde}`,
                      background: sel ? 'var(--well)' : 'var(--surface)',
                      color: sel ? 'var(--ink)' : T.tenue,
                    }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: m.color, flexShrink: 0 }} />
                    {m.nombre}
                  </button>
                );
              })}
            </div>
            {seleccionados.length === 0 && <Aviso tono="alerta">Sin ningún método marcado no se descuenta nada.</Aviso>}
          </Tarjeta>

          <Tarjeta titulo="Quién paga la comisión"
            descripcion="Lo que el negocio no absorbe se reparte entre los meseros, en proporción a la propina que ganó cada uno en el turno, y su saldo a entregar sale ya neto.">
            <Deslizador
              label="Absorbe el negocio" sufijo="%"
              valor={absorbe} min={0} max={100} step={5}
              onChange={v => actualizar('comisiones_absorbe', v)}
              izq="Todo a los meseros" der="Todo el negocio" />
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[
                { pct: 100, label: 'El negocio absorbe todo' },
                { pct: 50,  label: 'Mitad y mitad' },
                { pct: 0,   label: 'Lo pagan los meseros' },
              ].map(o => (
                <button key={o.pct} type="button" onClick={() => actualizar('comisiones_absorbe', String(o.pct))}
                  style={{
                    padding: '6px 12px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
                    fontSize: 14, fontWeight: absorbe === o.pct ? 600 : 400,
                    border: `1px solid ${absorbe === o.pct ? T.marcaTexto : T.borde}`,
                    background: absorbe === o.pct ? 'var(--well)' : 'var(--surface)',
                    color: absorbe === o.pct ? 'var(--ink)' : T.tenue,
                  }}>
                  {o.label}
                </button>
              ))}
            </div>
            {absorbe < 100 && (
              <Aviso tono="alerta">
                Descontar la comisión de la propina es práctica común, pero el artículo 346 de la Ley Federal
                del Trabajo considera la propina parte del ingreso del mesero. Confírmalo con tu contador antes
                de aplicarlo.
              </Aviso>
            )}
          </Tarjeta>

          <Tarjeta titulo="Así queda" descripcion="Ejemplo con un cobro de $1,000 en la terminal.">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 15 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: T.texto2 }}>
                <span>Cobrado al cliente</span><span>{moneda(1000)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--warn)' }}>
                <span>Comisión de la terminal ({pctEfectivo.toFixed(2)}% efectivo{fijo > 0 ? ` + ${moneda(fijo)} por cobro` : ''})</span>
                <span>−{moneda(comisionEjemplo)}</span>
              </div>
              <div style={{
                display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: T.texto,
                borderTop: `1px solid ${T.bordeSuave}`, paddingTop: 8,
              }}>
                <span>Te depositan</span><span>{moneda(1000 - comisionEjemplo)}</span>
              </div>
              {absorbe < 100 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, borderTop: `1px solid ${T.bordeSuave}`, paddingTop: 8, marginTop: 2 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: T.texto2 }}>
                    <span>De esa comisión, la paga el negocio</span>
                    <span>{moneda(comisionEjemplo * (absorbe / 100))}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--warn)' }}>
                    <span>Se descuenta de las propinas</span>
                    <span>{moneda(comisionEjemplo * (1 - absorbe / 100))}</span>
                  </div>
                </div>
              )}
            </div>
          </Tarjeta>
        </>
      )}
    </Seccion>
  );
}

/* ═══ FISCAL › Facturación ═══════════════════════════════════════════════ */
const REGIMENES_FISCALES = [
  { clave: '601', label: '601 - General de Ley Personas Morales' },
  { clave: '603', label: '603 - Personas Morales con Fines no Lucrativos' },
  { clave: '605', label: '605 - Sueldos y Salarios' },
  { clave: '606', label: '606 - Arrendamiento' },
  { clave: '608', label: '608 - Demás ingresos' },
  { clave: '612', label: '612 - Personas Físicas con Actividades Empresariales' },
  { clave: '621', label: '621 - Incorporación Fiscal' },
  { clave: '626', label: '626 - Régimen Simplificado de Confianza (RESICO)' },
];

const USOS_CFDI_CATALOGO = [
  { clave: 'G01', label: 'G01 - Adquisición de mercancías' },
  { clave: 'G03', label: 'G03 - Gastos en general' },
  { clave: 'P01', label: 'P01 - Por definir' },
];

export function SeccionFacturacion({ config, actualizar }: PropsConfig) {
  const usosHabilitados: string[] = (config.facturacion_usos_habilitados || 'G03').split(',').filter(Boolean);

  const toggleUso = (clave: string) => {
    const actuales = usosHabilitados.includes(clave)
      ? usosHabilitados.filter(c => c !== clave)
      : [...usosHabilitados, clave];
    actualizar('facturacion_usos_habilitados', actuales.join(','));
    // Si se desactivó el que era default, mover el default al primero que quede
    if (!actuales.includes(config.facturacion_uso_cfdi_default) && actuales.length > 0) {
      actualizar('facturacion_uso_cfdi_default', actuales[0]);
    }
  };

  return (
    <Seccion>
      <Tarjeta titulo="Ambiente" descripcion="Usa Pruebas con tu cuenta sandbox de Facturama antes de timbrar de verdad.">
        <Segmentado
          value={config.facturacion_ambiente || 'sandbox'}
          onChange={v => actualizar('facturacion_ambiente', v)}
          opciones={[
            { valor: 'sandbox',    label: 'Pruebas',    hint: 'No genera facturas reales' },
            { valor: 'produccion', label: 'Producción', hint: 'Timbra ante el SAT' },
          ]} />
      </Tarjeta>

      <Tarjeta titulo="Credenciales de Facturama">
        <Rejilla>
          <Campo label="Usuario">
            <Entrada value={config.facturacion_facturama_user || ''} placeholder="usuario@ejemplo.com"
              onChange={e => actualizar('facturacion_facturama_user', e.target.value)} />
          </Campo>
          <Campo label="Contraseña">
            <Entrada type="password" value={config.facturacion_facturama_password || ''} placeholder="••••••••"
              onChange={e => actualizar('facturacion_facturama_password', e.target.value)} />
          </Campo>
        </Rejilla>
      </Tarjeta>

      <Tarjeta titulo="Datos fiscales del emisor" descripcion="Los datos de tu negocio tal como están dados de alta ante el SAT.">
        <Rejilla>
          <Campo label="RFC">
            <Entrada value={config.facturacion_emisor_rfc || ''} placeholder="XAXX010101000"
              onChange={e => actualizar('facturacion_emisor_rfc', e.target.value.toUpperCase())} />
          </Campo>
          <Campo label="Código postal">
            <Entrada value={config.facturacion_emisor_cp || ''} placeholder="00000" maxLength={5}
              onChange={e => actualizar('facturacion_emisor_cp', e.target.value)} />
          </Campo>
          <div style={{ gridColumn: '1 / -1' }}>
            <Campo label="Razón social">
              <Entrada value={config.facturacion_emisor_nombre || ''} placeholder="Nombre o razón social del negocio"
                onChange={e => actualizar('facturacion_emisor_nombre', e.target.value)} />
            </Campo>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <Campo label="Régimen fiscal">
              <Selector value={config.facturacion_emisor_regimen || ''}
                onChange={e => actualizar('facturacion_emisor_regimen', e.target.value)}>
                <option value="">Selecciona…</option>
                {REGIMENES_FISCALES.map(r => <option key={r.clave} value={r.clave}>{r.label}</option>)}
              </Selector>
            </Campo>
          </div>
        </Rejilla>
      </Tarjeta>

      <Tarjeta titulo="Concepto de la factura" descripcion="Lo que se factura en cada CFDI, sin desglosar producto por producto.">
        <Campo label="Descripción del concepto">
          <Entrada value={config.facturacion_descripcion_concepto || ''} placeholder="Consumo de alimentos y bebidas"
            onChange={e => actualizar('facturacion_descripcion_concepto', e.target.value)} />
        </Campo>
        <Rejilla>
          <Campo label="Clave SAT producto/servicio" hint="90101501 = servicios de restaurante">
            <Entrada value={config.facturacion_clave_prod_serv || ''} placeholder="90101501"
              onChange={e => actualizar('facturacion_clave_prod_serv', e.target.value)} />
          </Campo>
          <Campo label="Clave SAT unidad" hint="E48 = unidad de servicio">
            <Entrada value={config.facturacion_clave_unidad || ''} placeholder="E48"
              onChange={e => actualizar('facturacion_clave_unidad', e.target.value)} />
          </Campo>
        </Rejilla>
        <Aviso>Los valores por defecto sirven para cualquier restaurante. No los cambies si no sabes qué son.</Aviso>
      </Tarjeta>

      <Tarjeta titulo="Usos de CFDI disponibles" descripcion="Los que aparecen como opción para el cliente al pedir su factura.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {USOS_CFDI_CATALOGO.map(u => {
            const activo = usosHabilitados.includes(u.clave);
            const esDefault = config.facturacion_uso_cfdi_default === u.clave;
            return (
              <div key={u.clave} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                padding: '8px 12px', border: `1px solid ${activo ? T.borde : T.bordeSuave}`, borderRadius: T.radio,
              }}>
                <label style={{ fontSize: 15, color: activo ? T.texto2 : T.debil, display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer' }}>
                  <input type="checkbox" checked={activo} onChange={() => toggleUso(u.clave)}
                    style={{ accentColor: T.marcaTexto, width: 15, height: 15, cursor: 'pointer' }} />
                  {u.label}
                </label>
                {activo && (
                  <button type="button" onClick={() => actualizar('facturacion_uso_cfdi_default', u.clave)}
                    style={{
                      fontSize: 13, fontWeight: 700, padding: '4px 10px', borderRadius: 999, cursor: 'pointer',
                      fontFamily: 'inherit', whiteSpace: 'nowrap',
                      border: `1px solid ${esDefault ? T.marcaTexto : T.borde}`,
                      background: esDefault ? T.marca : 'var(--surface)',
                      color: esDefault ? '#fff' : T.debil,
                    }}>
                    {esDefault ? 'Predeterminado' : 'Hacer predeterminado'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </Tarjeta>

      <Tarjeta titulo="Envío de factura por correo (SMTP)" descripcion="Opcional. Sin esto igual puedes timbrar y descargar el PDF y el XML a mano.">
        <Campo label="Servidor SMTP">
          <Entrada value={config.facturacion_smtp_host || ''} placeholder="smtp.gmail.com"
            onChange={e => actualizar('facturacion_smtp_host', e.target.value)} />
        </Campo>
        <Rejilla>
          <Campo label="Puerto">
            <Entrada value={config.facturacion_smtp_port || ''} placeholder="587"
              onChange={e => actualizar('facturacion_smtp_port', e.target.value)} />
          </Campo>
          <Campo label="Remitente">
            <Entrada value={config.facturacion_smtp_from || ''} placeholder="facturacion@tunegocio.mx"
              onChange={e => actualizar('facturacion_smtp_from', e.target.value)} />
          </Campo>
          <Campo label="Usuario">
            <Entrada value={config.facturacion_smtp_user || ''} placeholder="correo@tunegocio.mx"
              onChange={e => actualizar('facturacion_smtp_user', e.target.value)} />
          </Campo>
          <Campo label="Contraseña">
            <Entrada type="password" value={config.facturacion_smtp_password || ''} placeholder="••••••••"
              onChange={e => actualizar('facturacion_smtp_password', e.target.value)} />
          </Campo>
        </Rejilla>
      </Tarjeta>
    </Seccion>
  );
}
